import {
  List,
  VerticalTabs,
  VerticalTabsButton,
  VerticalTabsList,
  VerticalTabsPanel,
  Text,
  TextFontSizes,
  TextFontWeights,
  TextLineHeights,
  Tip,
  VerticalTabsTitle,
} from '@cinch-labs/design-system'
import { Wrapper } from '@cinch-nx/shared-ui'
import { interpolateI18n } from '@cinch-labs/shared-util'
import styles from './understand-car-finance.module.css'
import Link from 'next/link'
import { UnderstandCarFinanceText } from '../../types/locales'
import { useAnalytics } from '@cinch-nx/shared-analytics'
import {
  calculatorHpExplainedSelected,
  calculatorPcpExplainedSelected,
  understandingFinanceClick,
} from '../../tracking/tracking-events'

interface UnderstandCarFinanceProps {
  i18n: UnderstandCarFinanceText
}

function renderBoldP(text: string) {
  return (
    <Text
      key={text}
      fontWeight={TextFontWeights.bold}
      fontSize={TextFontSizes.l}
      className={styles.boldText}
      lineHeight={TextLineHeights.display}
    >
      {text}
    </Text>
  )
}

function renderP(text: string) {
  return (
    <Text
      key={text}
      fontSize={TextFontSizes.m}
      fontWeight={TextFontWeights.book}
      className={styles.text}
    >
      {text}
    </Text>
  )
}

function renderH4(text: string) {
  return (
    <Text
      key={text}
      element="h4"
      fontWeight={TextFontWeights.bold}
      lineHeight={TextLineHeights.display}
      fontSize={TextFontSizes.l}
      className={styles.smallTitle}
    >
      {text}
    </Text>
  )
}

function renderTip(text: string) {
  const content = text.split('<split>')

  if (content.length > 1) {
    return (
      <Tip
        key={text}
        titleEl="p"
        title={content[0]}
        text={content[1]}
        classNames={{ container: styles.info }}
      />
    )
  }

  return (
    <Tip key={text} text={content[0]} classNames={{ container: styles.info }} />
  )
}

function renderList(list: string) {
  const items = list.split('<split>')

  return (
    <List
      key={list}
      items={items}
      textProps={{ fontSize: TextFontSizes.m }}
      classNames={{ list: styles.list, item: styles.items }}
    />
  )
}

function renderBoldAndNormalText(text: string) {
  const content = text.split('<bold>')

  if (content.length > 1) {
    return (
      <p key={text}>
        <Text
          element="span"
          fontSize={TextFontSizes.m}
          fontWeight={TextFontWeights.bold}
          className={styles.boldText}
        >
          {content[0]}
        </Text>
        <Text element="span" fontSize={TextFontSizes.m} className={styles.text}>
          {content[1]}
        </Text>
      </p>
    )
  }

  return renderP(text)
}

function UnderstandingCarFinance({ i18n }: UnderstandCarFinanceProps) {
  const { title, sections, ariaLabel } = i18n
  const { trackEvent } = useAnalytics()

  const rteTags = {
    list: renderList,
    info: renderTip,
    h4: renderH4,
    p: renderP,
    boldP: renderBoldP,
    pcpLink: renderPcpLink,
    hpLink: renderHpLink,
    boldAndNormalText: renderBoldAndNormalText,
  }

  function renderPcpLink(text: string) {
    return (
      <Text className={styles.linkText} element="p" key={text}>
        <Link
          href="/car-finance/personal-contract-purchase"
          passHref
          legacyBehavior
        >
          <a
            target="_blank"
            rel="noreferrer noopener"
            href="/car-finance/personal-contract-purchase"
            onClick={() => trackEvent(calculatorPcpExplainedSelected())}
          >
            {text}
          </a>
        </Link>
      </Text>
    )
  }

  function renderHpLink(text: string) {
    return (
      <Text className={styles.linkText} element="p" key={text}>
        <Link href="/car-finance/hire-purchase" passHref legacyBehavior>
          <a
            target="_blank"
            rel="noreferrer noopener"
            href="/car-finance/hire-purchase"
            onClick={() => trackEvent(calculatorHpExplainedSelected())}
          >
            {text}
          </a>
        </Link>
      </Text>
    )
  }

  function renderRte(content: string) {
    return (
      <div className={styles.stack}>
        {interpolateI18n(content, undefined, rteTags)}
      </div>
    )
  }

  return (
    <article className={styles.understandingFinance}>
      <Wrapper>
        <div aria-labelledby="understand-car-finance-title">
          <Text
            element="h2"
            fontSize={TextFontSizes['4xl']}
            fontWeight={TextFontWeights.bold}
            lineHeight={TextLineHeights.display}
            className={styles.understandingFinanceTitle}
            id="understand-car-finance-title"
          >
            {title}
          </Text>
          <VerticalTabs className={styles.understandingFinanceVerticalTabs}>
            <VerticalTabsList ariaLabel={ariaLabel}>
              {sections.map((section, index) => (
                <VerticalTabsButton
                  id={`button-${index}`}
                  panelId={`understanding-finance-${index}`}
                  key={`button-${index}`}
                  onActive={() => {
                    trackEvent(understandingFinanceClick(title, section.title))
                  }}
                >
                  {section.title}
                </VerticalTabsButton>
              ))}
            </VerticalTabsList>
            {sections.map((section, index) => (
              <VerticalTabsPanel
                id={`understanding-finance-${index}`}
                buttonId={`button-${index}`}
                key={`understanding-finance-${index}`}
              >
                <VerticalTabsTitle>{section.title}</VerticalTabsTitle>
                {renderRte(section.content)}
              </VerticalTabsPanel>
            ))}
          </VerticalTabs>
        </div>
      </Wrapper>
    </article>
  )
}

export default UnderstandingCarFinance
